import React, {useCallback, useEffect, useMemo, useState, memo} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {PhishFitScoreProps} from './PhishFitScore.props';
import {useAppSelector} from '../../redux/hooks';
import {
    PhishFitScoreRow,
    getPhishFitScoreReportEvent,
    managerTeamDataSelector,
} from '../../redux/managerTeamSlice';
import {DataTables} from '../../components/DataTables';
import {GridColDef, GridRenderCellParams, GridSortModel} from '@mui/x-data-grid';
import {
    Box,
    Button,
    Stack,
    styled,
    Typography,
    TypographyProps,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {UserAvatarLetters} from '../../components/UserAvatarLetters';
import {CustomPagination} from '../../components/DataTables/components/CustomPagination';
import {LoadingPage} from '../../components/LoadingPage';
import './PhishFitScore.css';
import {ScoreCircle} from "../../components/ScoreCircle";
import {SearchMenuBar} from "../../components/SearchMenuBar";
import {getCurrentCellStore, getDownloadTimestamp, roundPhishFitScore} from '../../utils/util';
import { downloadPhishFitScoreReport } from '../../api/platform';
import {DownloadButton} from '../../components/DownloadButton';
import { useDownloadReport } from '../../components/DownloadButton/useDownloadReport';
import { SnackBar } from '../../components/SnackBar';

interface PhishFitScoreDataRow extends PhishFitScoreRow {
    id: number;
}

const defaultSortModel: GridSortModel = [{field: 'first_name', sort: 'asc'}];

type ViewProfileButtonProps = {
    row: PhishFitScoreDataRow;
    onView: (row: PhishFitScoreDataRow) => void;
};

const ViewProfileButton = memo(({row, onView}: ViewProfileButtonProps) => {
    const handleClick = useCallback(() => {
        onView(row);
    }, [onView, row]);

    return (
        <Button
            variant="outlined"
            className="view-profile-btn"
            onClick={handleClick}
        >
            View Profile
        </Button>
    );
});

export const PhishFitScore: React.FC<PhishFitScoreProps> = () => {
    const [filterSearchKeyword, setFilterSearchKeyword] = useState('');
    const [rows, setRows] = useState<PhishFitScoreDataRow[]>([]);
    const [totalPage, setTotalPage] = useState(0);
    const [totalRows, setTotalRows] = useState(0);
    const [startPage, setStartPage] = useState(0);
    const [endPage, setEndPage] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const dispatch = useDispatch<any>();
    const navigate = useNavigate();
    const currentCell = getCurrentCellStore();
    const showActionsColumn = currentCell?.team_setting?.phishfit_score?.allow_manager_access !== 0;

    const {isPhishFitScoreLoading, phishFitScoreData} = useAppSelector(managerTeamDataSelector);

    const theme = useTheme();
    const isLaptop = useMediaQuery(theme.breakpoints.between('tablet', 'laptop'));
    const filename = () => `phish-fit-${getDownloadTimestamp()}.csv`;

    const defaultParams = {
        per_pages: 10,
        page: 1,
        keyword: '',
        sort_type: 'asc',
        sort_column: 'first_name',
    };
    const [mainParams, setMainParams] = useState<any>(defaultParams);

    const fetchData = async (params: any) => {
        setMainParams(params);
        await dispatch(getPhishFitScoreReportEvent(params));
    };

    useEffect(() => {
        fetchData(defaultParams);
    }, [dispatch]);

    useEffect(() => {
        const data: PhishFitScoreDataRow[] = phishFitScoreData.list.map((result, index) => ({
            ...result,
            id: index
        }));
        setRows(data);
        setTotalPage(phishFitScoreData['total_page']);
        setTotalRows(phishFitScoreData['total_items']);
        setStartPage(phishFitScoreData['start_item']);
        setEndPage(phishFitScoreData['end_item']);
    }, [phishFitScoreData]);

    const handleSortModelChange = (newSortModel: GridSortModel) => {
        const model = newSortModel?.length > 0 ? newSortModel : defaultSortModel;
        const params = {
            ...mainParams,
            sort_type: model[0].sort,
            sort_column: model[0].field
        };
        fetchData(params);
    };

    const onSearchByKeyword = (event: any) => {
        if (event.keyCode === 13) {
            const params = {
                ...mainParams,
                keyword: event.target.value,
                page: 1
            };
            setCurrentPage(1);
            fetchData(params);
        }
    };

    const onRowsPerPageChange = (perPage: number) => {
        const params = {
            ...mainParams,
            per_pages: perPage,
            page: 1
        };
        setPerPage(perPage);
        setCurrentPage(1);
        fetchData(params);
    };

    const onChangePage = (currentPage: number) => {
        const params = {
            ...mainParams,
            page: currentPage,
        };
        setCurrentPage(currentPage);
        fetchData(params);
    };

    const handleViewProfile = useCallback((row: any) => {
        navigate(`/team/learner-profile/${row.learner_id}`, {
            state: {
                ...currentCell,
                learner_id: row.learner_id,
                student_id: row.learner_id,
                email: row.email,
                isManagerView: true,
                first_name: row.first_name,
                last_name: row.last_name,
            }
        });
    }, [navigate, currentCell]);

    const columns: GridColDef[] = useMemo(() => [
        {
            field: 'first_name',
            headerName: 'Learners',
            flex: isLaptop ? 1.5 : 2,
            sortable: false,
            headerClassName: 'phish-fit-score-header-start',
            renderCell: (params: GridRenderCellParams) => {
                const firstName = params.row.first_name ? params.row.first_name.trim() : '';
                const lastName = params.row.last_name ? params.row.last_name.trim() : '';
                const learnerName = firstName + ' ' + lastName;
                return (
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.875}
                        className={'phish-fit-score-cell'}
                    >
                        <UserAvatarLetters name={learnerName} classNames={'phish-fit-score-user-name'}/>
                        <Stack>
                            <CellText>{learnerName}</CellText>
                            <CellText color="secondary.main" fontWeight={700} style={{lineBreak: 'anywhere'}}>
                                <a className={'phish-fit-score-cell-email'} href={`mailto:${params.row.email}`}>
                                    {params.row.email}
                                </a>
                            </CellText>
                        </Stack>
                    </Stack>
                );
            }
        },
        {
            field: 'click_through_rate',
            headerName: 'Click Through Rate',
            flex: 1,
            sortable: true,
            renderCell: (params: GridRenderCellParams) => {
                return <CellText>{params.row.click_through_rate}%</CellText>;
            }
        },
        {
            field: 'course_completion',
            headerName: 'Course Completion',
            flex: 1,
            sortable: true,
            renderCell: (params: GridRenderCellParams) => {
                return <CellText>{params.row.course_completion}%</CellText>;
            }
        },
        {
            field: 'phish_fit_score',
            headerName: 'PhishFit Score',
            flex: 1,
            sortable: true,
            align: showActionsColumn ? 'left' : 'right' as const,
            headerAlign: showActionsColumn ? 'left' : 'right' as const,
            renderCell: (params: GridRenderCellParams) => {
                const score = params.row.phish_fit_score;
                return (
                    <Typography className={showActionsColumn ? 'display-score-circle' : 'score-circle-aligned-right'} sx={{ width: '100%'}}>
                        {score !== null && score !== undefined ? (
                            <ScoreCircle score={roundPhishFitScore(score)} progress={100}/>
                        ) : (
                            <Typography variant="body2" textAlign="left" sx={{fontSize: '14px', fontWeight: 400}}>
                                            No Score
                            </Typography>
                        )}
                    </Typography>
                );
            }
        },
        ...(showActionsColumn ? [{
            field: 'actions',
            headerName: 'Actions',
            flex: 0.8,
            align: 'right' as const,
            headerAlign: 'right' as const,
            sortable: false,
            renderCell: (params: GridRenderCellParams<PhishFitScoreDataRow>) => (
                <ViewProfileButton
                    row={params.row}
                    onView={handleViewProfile}
                />
            )
        }] : [])
    ], [showActionsColumn, isLaptop, handleViewProfile]);

    const {
        handleDownload,
        isDownloading,
        label: downloadLabel,
        showSnackbar,
        setShowSnackbar,
        snackbarMessage,
        snackbarSeverity
    } = useDownloadReport(
        () => downloadPhishFitScoreReport({
        keyword: mainParams.keyword,
        sort_column: mainParams.sort_column,
        sort_type: mainParams.sort_type,
        }),
        [
            { key: 'first_name',         label: 'First Name' },
            { key: 'last_name',          label: 'Last Name' },
            { key: 'email',              label: 'Email' },
            { key: 'click_through_rate', label: 'Click Through Rate (%)' },
            { key: 'course_completion',  label: 'Course Completion (%)' },
            {
                key: 'phish_fit_score',
                label: 'PhishFit Score',
                format: (row) =>
                    row.phish_fit_score !== null && row.phish_fit_score !== undefined
                        ? roundPhishFitScore(row.phish_fit_score)
                        : 'No Score'
            },
        ],
        filename
    );

    return (
        <Stack spacing={0} className="phish-fit-score-container">
            <SearchMenuBar
                selectedItems={[]}
                handleEnterSearch={onSearchByKeyword}
                searchInputValue={filterSearchKeyword}
                setSearchInputValue={setFilterSearchKeyword}
                isShowFilter={false}
            />
            <Box className="manager-xp-additional-function">
                <DownloadButton
                    onDownload={handleDownload}
                    loading={isDownloading}
                    disabled={isDownloading}
                    label={downloadLabel}
                    className="manager-xp-download-button"
                />
            </Box>
            <DataTables
                tblRows={rows}
                tblColumns={columns}
                totalRows={totalRows}
                sortingMode={'server'}
                sortModelChangeHandler={handleSortModelChange}
                customPaging={
                    <CustomPagination
                        rowsPerPageOptions={[5, 10, 20]}
                        currentPage={currentPage}
                        totalPage={totalPage}
                        perPage={perPage}
                        startPage={startPage}
                        endPage={endPage}
                        totalRows={totalRows}
                        setCurrentPage={setCurrentPage}
                        setPerPage={setPerPage}
                        onChangePage={onChangePage}
                        onRowsPerPageChange={onRowsPerPageChange}
                    />
                }
            />
            <LoadingPage open={isPhishFitScoreLoading}/>
            <SnackBar
                open={showSnackbar}
                severity={snackbarSeverity}
                message={snackbarMessage}
                onClose={() => setShowSnackbar(false)}
            />
        </Stack>
    );
};

const CellText = styled((props: TypographyProps) => (
    <Typography fontWeight={500} fontSize={14} {...props} />
))((props: TypographyProps) => ({
    sx: {fontWeight: props.fontWeight, fontSize: props.fontSize}
}));
